import { useState, useEffect, useCallback } from 'react';
import isNil from 'lodash/isNil';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { ERROR_REPORTING_TEAM, PACKAGE_NAME, SERVICES_ERROR_ID } from '../../common/constants.tsx';
import type { Value } from './types.tsx';

export const useFetchCustomFieldValues = ({
	planId,
	scenarioId,
	fieldId,
}: {
	planId: string | undefined;
	scenarioId: string | undefined;
	fieldId: string | undefined;
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const [values, setValues] = useState<Value[] | null>(null);
	const fetchCustomFieldValues = useCallback(
		async (query = '') => {
			try {
				if (isNil(planId)) {
					throw new Error('planId is empty in the creation IP board');
				}
				if (isNil(scenarioId)) {
					throw new Error('scenarioId is empty in the creation IP board');
				}
				if (isNil(fieldId)) {
					throw new Error('fieldId is empty in the creation IP board');
				}
				setIsLoading(true);
				const url = `/rest/jpo/2.0/plans/${planId}/increments/scenarios/${scenarioId}/fieldId/${fieldId}/scopeFilterValues?query=${query}`;
				// eslint-disable-next-line @typescript-eslint/no-shadow
				const values = await performGetRequest(url);
				setValues(values || []);
				setError(null);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-shadow
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: SERVICES_ERROR_ID,
						packageName: PACKAGE_NAME,
						teamName: ERROR_REPORTING_TEAM,
					},
					attributes: {
						serviceName: 'useFetchCustomFieldValues',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
				setError(error);
				setValues(null);
			} finally {
				setIsLoading(false);
			}
		},
		[planId, scenarioId, fieldId],
	);

	useEffect(() => {
		if (isNil(fieldId)) {
			setValues(null);
		}
	}, [fieldId]);

	return {
		isLoading,
		error,
		values,
		fetchCustomFieldValues,
	};
};
